import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import Webcam from "react-webcam";
import SignatureCanvas from 'react-signature-canvas';
import {
    Truck,
    Clock,
    Camera,
    CheckCircle,
    MapPin,
    Users,
    X,
    Image as ImageIcon,
    PenTool,
    ShieldCheck,
    Loader2,
    RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function CenterAutoManagement() {
    const { token, user } = useAuth();
    const [autos, setAutos] = useState([]);
    const [tripsHistory, setTripsHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('new'); // 'new', 'history'

    // Verification Modal State
    const [showVerificationModal, setShowVerificationModal] = useState(false);

    const [formData, setFormData] = useState({
        auto_id: '',
        student_count: '',
        student_names: '',
        pickup_location: '',
        drop_location: 'Central Branch' // Default or user editable
    });

    // Camera State (on main form)
    const webcamRef = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [facingMode, setFacingMode] = useState("environment");

    // Signature State (in modal)
    const sigPad = useRef(null);

    useEffect(() => {
        fetchAutos();
        fetchHistory();
    }, []);

    const fetchAutos = async () => {
        try {
            const res = await api.get('/autos');
            setAutos(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await api.get('/autos/history');
            if (res.data && res.data.data) {
                setTripsHistory(res.data.data);
            } else if (Array.isArray(res.data)) {
                setTripsHistory(res.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handlePreSubmit = (e) => {
        e.preventDefault();

        if (!formData.auto_id) return alert("Please select an Auto");
        if (!formData.pickup_location) return alert("Please enter the Pick-up Location");
        if (!formData.drop_location) return alert("Please enter the Drop Location");
        if (!formData.student_count) return alert("Please enter student count");

        const count = parseInt(formData.student_count, 10);
        if (count > 0) {
            if (!formData.student_names || formData.student_names.trim() === '') {
                return alert(`Security Check: Please enter the names of all ${count} students.`);
            }

            // split by comma, remove extra spaces, filter out empty names
            const namesArray = formData.student_names.split(',').map(name => name.trim()).filter(name => name !== '');
            if (namesArray.length !== count) {
                return alert(`Security Check: You entered ${count} for Total Students, but provided ${namesArray.length} names. Please provide exactly ${count} comma-separated names.`);
            }
        }

        if (!capturedImage) return alert("Please capture a photo with Driver, Auto, and Students first.");

        // Open modal for signature verification
        setShowVerificationModal(true);
    };

    const handleCapture = () => {
        const imageSrc = webcamRef.current.getScreenshot();
        setCapturedImage(imageSrc);
        setIsCameraOpen(false);
    };

    const dataURLtoFile = (dataurl, filename) => {
        let arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    };

    const clearSignature = () => {
        if (sigPad.current) {
            sigPad.current.clear();
        }
    };

    const closeVerificationModal = () => {
        setShowVerificationModal(false);
    };

    const handleFinalSubmit = async () => {
        if (sigPad.current.isEmpty()) return alert("Driver Signature is required to confirm arrival.");

        setLoading(true);

        const payload = new FormData();
        payload.append('auto_id', formData.auto_id);
        payload.append('student_count', formData.student_count || 0);
        payload.append('student_names', String(formData.student_names || '').trim());
        payload.append('pickup_location', formData.pickup_location);
        payload.append('drop_location', formData.drop_location);

        const photoFile = dataURLtoFile(capturedImage, `trip_${Date.now()}.jpg`);
        payload.append('trip_photo', photoFile);

        // Get Signature
        const signatureData = sigPad.current.getCanvas().toDataURL('image/png');
        payload.append('signature', signatureData);

        try {
            await api.post('/autos/trips/center-log', payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert("Arrival Trip logged successfully!");
            setFormData({
                auto_id: '',
                student_count: '',
                student_names: '',
                pickup_location: '',
                drop_location: 'Central Branch'
            });
            setCapturedImage(null);
            closeVerificationModal();
            fetchHistory();
            setActiveTab('history');
        } catch (error) {
            console.error(error);
            alert("Failed to log trip. " + (error.response?.data?.message || ''));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-4 py-3 sm:px-5 sm:py-4 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h2 className="text-base sm:text-lg font-bold flex items-center gap-2 m-0 text-white">
                        <Truck className="w-5 h-5 text-indigo-200" /> Auto Arrival Log
                    </h2>
                    <p className="text-indigo-100 text-[10px] sm:text-xs mt-0.5 m-0 p-0">Record incoming student trips and verify driver</p>
                </div>
                <div className="flex bg-white/10 p-1 rounded w-full sm:w-max">
                    <button
                        onClick={() => setActiveTab('new')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 font-semibold text-xs rounded transition-colors ${activeTab === 'new' ? 'bg-white text-indigo-700 shadow' : 'text-white hover:bg-white/10'}`}
                    >
                        Log Arrival
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 font-semibold text-xs rounded transition-colors ${activeTab === 'history' ? 'bg-white text-indigo-700 shadow' : 'text-white hover:bg-white/10'}`}
                    >
                        Today's Trips
                    </button>
                </div>
            </div>

            {activeTab === 'new' && (
                <div className="p-4 sm:p-6 bg-slate-50">
                    <form onSubmit={handlePreSubmit} className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">

                        <div className="grid grid-cols-1 md:grid-cols-2">
                            {/* Left Side: Detail Forms */}
                            <div className="p-4 sm:p-5 border-b md:border-b-0 md:border-r border-slate-200">
                                <div className="mb-4 pb-2 border-b border-slate-100">
                                    <h3 className="text-sm font-bold text-slate-700 uppercase flex items-center gap-2 m-0">
                                        <MapPin size={16} className="text-indigo-500" /> Trip Details
                                    </h3>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-semibold text-slate-600 block mb-1 uppercase tracking-wide">Select Arrival Auto</label>
                                        <select
                                            className="w-full h-10 bg-slate-50 border border-slate-300 rounded px-3 text-sm focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                                            value={formData.auto_id}
                                            onChange={(e) => setFormData({ ...formData, auto_id: e.target.value })}
                                            required
                                        >
                                            <option value="" disabled>Select vehicle and driver...</option>
                                            {autos
                                                .filter(auto => !user?.center_name || auto.center_name === user.center_name)
                                                .map(auto => (
                                                    <option key={auto.id} value={auto.id}>
                                                        {auto.vehicle_no} - {auto.driver?.name} ({auto.center_name})
                                                    </option>
                                                ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide flex items-center gap-1.5"><Users size={14} /> Total Students</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="w-full h-10 bg-slate-50 border border-slate-300 rounded px-3 text-sm focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                                            value={formData.student_count}
                                            onChange={(e) => setFormData({ ...formData, student_count: e.target.value })}
                                            placeholder="e.g. 5"
                                            required
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-slate-600 block mb-1 uppercase tracking-wide">From Where (Pick-up)</label>
                                            <input
                                                type="text"
                                                className="w-full h-10 bg-slate-50 border border-slate-300 rounded px-3 text-sm focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                                                value={formData.pickup_location}
                                                onChange={(e) => setFormData({ ...formData, pickup_location: e.target.value })}
                                                placeholder="e.g. Village Name, Area"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-slate-600 block mb-1 uppercase tracking-wide">To Where (Drop)</label>
                                            <input
                                                type="text"
                                                className="w-full h-10 bg-slate-50 border border-slate-300 rounded px-3 text-sm focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                                                value={formData.drop_location}
                                                onChange={(e) => setFormData({ ...formData, drop_location: e.target.value })}
                                                placeholder="e.g. Center Name"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold text-slate-600 block mb-1 uppercase tracking-wide">Student Names (Comma separated)</label>
                                        <textarea
                                            className="w-full bg-slate-50 border border-slate-300 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500 outline-none min-h-[80px] resize-none transition-colors"
                                            value={formData.student_names}
                                            onChange={(e) => setFormData({ ...formData, student_names: e.target.value })}
                                            placeholder="Ravi, Priya..."
                                        ></textarea>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Camera */}
                            <div className="p-4 sm:p-5 flex flex-col">
                                <div className="mb-4 pb-2 border-b border-slate-100">
                                    <h3 className="text-sm font-bold text-slate-700 uppercase flex items-center gap-2 m-0">
                                        <Camera size={16} className="text-indigo-500" /> Evidence Photo
                                    </h3>
                                    <p className="text-[10px] text-slate-500 mt-1">Capture Driver, Auto, and Students.</p>
                                </div>

                                <div className="border border-slate-300 rounded bg-black flex-1 min-h-[200px] sm:min-h-[250px] relative overflow-hidden flex flex-col">
                                    {!capturedImage ? (
                                        <>
                                            {!isCameraOpen ? (
                                                <div className="flex-1 flex flex-col items-center justify-center p-4 text-center bg-slate-100">
                                                    <ImageIcon className="w-10 h-10 text-slate-300 mb-2" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsCameraOpen(true)}
                                                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs sm:text-sm py-2 px-4 rounded shadow-sm flex items-center gap-2 transition-colors mt-2"
                                                    >
                                                        <Camera size={16} /> Start Camera
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex-1 relative flex flex-col">
                                                    <Webcam
                                                        audio={false}
                                                        ref={webcamRef}
                                                        screenshotFormat="image/jpeg"
                                                        videoConstraints={{ facingMode: facingMode }}
                                                        className="w-full h-full object-cover"
                                                        style={{ flex: 1 }}
                                                    />
                                                    <div className="absolute top-2 right-2 z-10">
                                                        <button
                                                            type="button"
                                                            onClick={() => setFacingMode(prev => prev === "environment" ? "user" : "environment")}
                                                            className="bg-black/40 hover:bg-black/60 text-white p-2 sm:p-2.5 rounded-full transition-colors backdrop-blur-sm shadow-sm"
                                                            title="Switch Camera"
                                                        >
                                                            <RefreshCw size={18} />
                                                        </button>
                                                    </div>
                                                    <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-4 px-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleCapture}
                                                            className="w-12 h-12 sm:w-14 sm:h-14 bg-white border-2 border-indigo-500 text-indigo-600 hover:bg-slate-100 rounded-full flex items-center justify-center shadow-lg transition-colors"
                                                        >
                                                            <Camera size={20} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="flex-1 relative flex flex-col">
                                            <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none"></div>
                                            <div className="absolute bottom-3 left-0 right-0 flex justify-center z-10 px-2">
                                                <button
                                                    type="button"
                                                    onClick={() => { setCapturedImage(null); setIsCameraOpen(true); }}
                                                    className="bg-white/90 backdrop-blur text-slate-800 text-[10px] font-bold uppercase px-3 py-1.5 rounded shadow hover:bg-white transition-colors"
                                                >
                                                    Retake Photo
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Submit Form action -> triggers modal */}
                        <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50">
                            <button
                                type="submit"
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded font-bold text-[11px] sm:text-sm shadow flex justify-center items-center gap-1.5 sm:gap-2 transition-colors uppercase tracking-widest sm:tracking-wide leading-tight"
                            >
                                <span>Proceed to Driver Verification</span> <ShieldCheck size={16} className="shrink-0" />
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {
                activeTab === 'history' && (
                    <div className="p-4 sm:p-5 bg-slate-50 min-h-[400px]">
                        {tripsHistory.length === 0 ? (
                            <div className="text-center py-16 bg-white border border-slate-200 rounded-lg max-w-2xl mx-auto shadow-sm">
                                <Clock className="mx-auto w-10 h-10 text-slate-300 mb-2" />
                                <h3 className="text-sm font-semibold text-slate-600 m-0">No Arrivals Logged Today</h3>
                                <p className="text-slate-400 text-xs mt-1">Arrival trips recorded by you will appear here.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {tripsHistory.filter(t => t.trip_type === 'pickup').map((trip, i) => (
                                    <div key={i} className="bg-white border text-left border-slate-200 rounded flex flex-col overflow-hidden hover:shadow-md transition-shadow">
                                        <div className="h-32 bg-slate-100 relative shrink-0">
                                            <div className="absolute top-2 right-2 bg-black/60 backdrop-blur text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">
                                                {new Date(trip.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                            {trip.evidence_path ? (
                                                <img src={`${window.location.hostname === 'localhost' ? 'http://localhost:8000' : '/backend/public'}/storage/${trip.evidence_path}`} alt="Trip" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={24} /></div>
                                            )}
                                        </div>
                                        <div className="p-3 flex flex-col flex-1">
                                            <div className="flex justify-between items-start mb-2 shrink-0">
                                                <div>
                                                    <h4 className="font-bold text-indigo-700 text-sm leading-tight m-0">{trip.auto?.vehicle_no}</h4>
                                                    <p className="text-[10px] text-slate-500 font-semibold mt-0.5 uppercase">{trip.driver?.name || 'Unknown'}</p>
                                                </div>
                                                <div className="bg-indigo-50 px-2 py-1 rounded border border-indigo-100 text-center min-w-[40px]">
                                                    <div className="text-[10px] font-bold text-indigo-600 block">{trip.student_count} <span className="text-[8px] text-indigo-400 font-normal">Stds</span></div>
                                                </div>
                                            </div>
                                            <div className="text-[10px] text-slate-600 line-clamp-3 bg-slate-50 p-1.5 rounded border border-slate-100 mt-1 mb-2 flex-1">
                                                <span className="font-semibold text-slate-500 uppercase">Names:</span> {trip.student_names || 'N/A'}
                                            </div>
                                            {trip.signature_path && (
                                                <div className="pt-2 border-t border-slate-100 flex items-center justify-between shrink-0">
                                                    <span className="text-[10px] text-slate-500 font-semibold flex items-center gap-1 uppercase"><CheckCircle size={10} className="text-green-500" /> Driver Verified</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )
            }

            {/* VERIFICATION MODAL */}
            <AnimatePresence>
                {showVerificationModal && (
                    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-lg w-full max-w-sm sm:max-w-md shadow-2xl relative flex flex-col overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="bg-slate-900 px-4 py-3 flex justify-between items-center shrink-0">
                                <div className="flex items-center gap-2">
                                    <ShieldCheck className="text-green-400 w-5 h-5" />
                                    <h3 className="text-sm font-bold text-white m-0 uppercase tracking-widest">Driver Verification</h3>
                                </div>
                                <button
                                    onClick={closeVerificationModal}
                                    className="text-slate-400 hover:text-white transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="p-4 bg-slate-50 flex flex-col gap-4">
                                {/* Live Photo Verification Display */}
                                <div>
                                    <span className="text-xs font-bold text-slate-600 uppercase flex items-center gap-1.5 mb-2">
                                        <Camera size={14} /> 1. Live Evidence Snapshot
                                    </span>
                                    <div className="rounded border border-slate-200 overflow-hidden bg-black h-36 relative">
                                        <img src={capturedImage} alt="Verification" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 shadow-inner"></div>
                                    </div>
                                </div>

                                {/* Signature Area */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-slate-600 uppercase flex items-center gap-1.5">
                                            <PenTool size={14} /> 2. Driver Signature
                                        </span>
                                        <button type="button" onClick={clearSignature} className="text-[10px] bg-red-50 text-red-600 px-2 py-0.5 rounded font-semibold border border-red-200">Clear</button>
                                    </div>

                                    <div className="border border-slate-300 rounded overflow-hidden mt-1 bg-white min-h-[120px] relative shadow-sm">
                                        <SignatureCanvas
                                            ref={sigPad}
                                            penColor='black'
                                            canvasProps={{ className: 'w-full h-full absolute inset-0 signature-canvas', style: { touchAction: 'none' } }}
                                            backgroundColor='rgba(255,255,255,0)'
                                        />
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-2 text-center">Driver must sign inside the box to confirm arrival.</p>
                                </div>
                            </div>

                            {/* Action Button */}
                            <div className="p-4 border-t border-slate-100 bg-white">
                                <button
                                    onClick={handleFinalSubmit}
                                    disabled={loading}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded font-bold text-[11px] sm:text-sm shadow flex justify-center items-center gap-1.5 sm:gap-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-widest sm:tracking-wide leading-tight"
                                >
                                    {loading ? 'Processing...' : (
                                        <><span>Confirm Arrival Form</span> <CheckCircle size={16} className="shrink-0" /></>
                                    )}
                                </button>
                            </div>

                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

        </div >
    );
}
